import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../components/Toast';
import { useAuth } from '../AuthContext';
import { useUserProfile } from './useUserProfile';
import { planLabel } from './planTypes';
import {
  THEME_OPTIONS,
  readThemePreference,
  setThemePreference,
} from './yogaTheme';

const LEVELS = [
  { id: 'beginner', label: 'Beginner' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced', label: 'Advanced' },
];

const EMPTY = {
  displayName: '',
  practiceLevel: 'beginner',
  goals: '',
  preferredMins: 30,
  equipment: '',
};

function tierClass(planType) {
  if (planType === 'karma') return 'yoga-tier yoga-tier--karma';
  if (planType === 'guided') return 'yoga-tier yoga-tier--guided';
  if (planType === 'teacher') return 'yoga-tier yoga-tier--teacher';
  return 'yoga-tier yoga-tier--guided';
}

export default function Profile() {
  const toast = useToast();
  const { user } = useAuth();
  const { profile, planType, loading, error, saveProfileFields } = useUserProfile();
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [theme, setTheme] = useState(() => readThemePreference());

  const onTheme = (id) => {
    setTheme(setThemePreference(id));
  };

  useEffect(() => {
    if (!profile) return;
    setDraft({
      displayName: profile.displayName || '',
      practiceLevel: profile.practiceLevel || 'beginner',
      goals: profile.goals || '',
      preferredMins: Number(profile.preferredMins) || 30,
      equipment: profile.equipment || '',
    });
  }, [profile]);

  const onSave = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await saveProfileFields({
        displayName: draft.displayName.trim().slice(0, 80),
        practiceLevel: draft.practiceLevel,
        goals: draft.goals.trim().slice(0, 400),
        preferredMins: Math.max(5, Math.min(120, Number(draft.preferredMins) || 30)),
        equipment: draft.equipment.trim().slice(0, 400),
      });
      toast('Profile saved', 'ok');
    } catch (err) {
      toast(`Could not save: ${err.message}`, 'danger', 8000);
    } finally {
      setBusy(false);
    }
  };

  const streak = Number(profile?.streakDays) || 0;
  const sessions = Number(profile?.totalSessions) || 0;
  const name = draft.displayName?.trim() || profile?.displayName?.trim() || '';
  const initial = (name || user?.email || 'Y').slice(0, 1).toUpperCase();

  return (
    <div className="cs-page yoga-shell u-stack m-fade-in" style={{ '--gap': 'var(--sp-6)' }}>
      <header className="yoga-page-head m-rise">
        <p className="yoga-eyebrow">YogaKai</p>
        <h1 className="yoga-hero-title">Profile</h1>
        <p className="yoga-hero-lead">
          Practice preferences shape Today’s recommendation. Streak, sessions, and recent history update when you finish a class.
        </p>
      </header>

      <div className="yoga-profile-form" style={{ paddingBottom: 18 }}>
        <fieldset className="u-stack" style={{ '--gap': '8px', border: 0, padding: 0, margin: 0 }}>
          <legend className="yoga-eyebrow">Appearance</legend>
          <p className="u-muted u-small" style={{ margin: 0 }}>
            Light is the sunlit studio. Dark is a moss-night practice room — same sage and amber, softer light.
          </p>
          <div className="u-cluster" style={{ '--gap': '8px' }} role="group" aria-label="Appearance">
            {THEME_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className="cs-chip"
                aria-pressed={theme === opt.id}
                onClick={() => onTheme(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </fieldset>
      </div>

      {error && (
        <div className="cs-banner b-danger" role="alert">
          Could not load profile: {error}
        </div>
      )}

      {loading && (
        <p className="cs-help">
          <span className="cs-spinner" aria-hidden /> Loading profile…
        </p>
      )}

      {!loading && !error && (
        <>
          <div className="yoga-phero">
            <div className="yoga-avatar" aria-hidden>{initial}</div>
            <div className="u-stack" style={{ '--gap': '6px' }}>
              <strong style={{ fontSize: '1.15rem', fontWeight: 600 }}>
                {name || 'Your practice'}
              </strong>
              {user?.email ? (
                <span className="u-muted u-small">{user.email}</span>
              ) : null}
              {planType ? (
                <span className={tierClass(planType)}>{planLabel(planType)}</span>
              ) : null}
            </div>
          </div>

          <div className="yoga-panel" style={{ maxWidth: 520 }}>
            <div className="yoga-today-stats">
              <div className="yoga-today-stat-cell">
                Streak
                <span className="yoga-today-stat">
                  {streak}
                  <small style={{ fontSize: '0.7rem', marginLeft: 4 }}>
                    day{streak === 1 ? '' : 's'}
                  </small>
                </span>
              </div>
              <div className="yoga-today-stat-cell">
                Sessions
                <span className="yoga-today-stat">{sessions}</span>
              </div>
              <div className="yoga-today-stat-cell">
                Plan
                <span className="yoga-today-stat" style={{ fontSize: '0.95rem' }}>
                  {planType ? planLabel(planType) : '—'}
                </span>
              </div>
            </div>
          </div>

          <form className="yoga-profile-form" onSubmit={onSave}>
            <label className="u-stack" style={{ '--gap': '6px' }}>
              <span className="yoga-eyebrow">Display name</span>
              <input
                className="cs-input"
                value={draft.displayName}
                onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                maxLength={80}
                disabled={busy}
              />
            </label>

            <label className="u-stack" style={{ '--gap': '6px' }}>
              <span className="yoga-eyebrow">Email</span>
              <input
                className="cs-input"
                value={user?.email || ''}
                readOnly
                disabled
                aria-readonly
              />
            </label>

            <fieldset className="u-stack" style={{ '--gap': '8px', border: 0, padding: 0, margin: 0 }}>
              <legend className="yoga-eyebrow">Practice level</legend>
              <div className="u-cluster" style={{ '--gap': '8px' }}>
                {LEVELS.map((lv) => (
                  <button
                    key={lv.id}
                    type="button"
                    className="cs-chip"
                    aria-pressed={draft.practiceLevel === lv.id}
                    disabled={busy}
                    onClick={() => setDraft({ ...draft, practiceLevel: lv.id })}
                  >
                    {lv.label}
                  </button>
                ))}
              </div>
            </fieldset>

            <label className="u-stack" style={{ '--gap': '6px' }}>
              <span className="yoga-eyebrow">Preferred session length (minutes)</span>
              <input
                className="cs-input"
                type="number"
                min={5}
                max={120}
                step={5}
                value={draft.preferredMins}
                onChange={(e) => setDraft({ ...draft, preferredMins: e.target.value })}
                disabled={busy}
              />
            </label>

            <label className="u-stack" style={{ '--gap': '6px' }}>
              <span className="yoga-eyebrow">Goals</span>
              <textarea
                className="cs-input"
                rows={3}
                value={draft.goals}
                onChange={(e) => setDraft({ ...draft, goals: e.target.value })}
                disabled={busy}
                placeholder="e.g. morning mobility, calm evenings…"
                style={{ resize: 'vertical' }}
              />
            </label>

            <label className="u-stack" style={{ '--gap': '6px' }}>
              <span className="yoga-eyebrow">Equipment</span>
              <input
                className="cs-input"
                value={draft.equipment}
                onChange={(e) => setDraft({ ...draft, equipment: e.target.value })}
                disabled={busy}
                placeholder="Mat, blocks, strap…"
              />
            </label>

            <button className="cs-btn primary" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save profile'}
            </button>
          </form>

          <p className="u-muted u-small" style={{ margin: 0 }}>
            <Link to="/membership">Change membership plan</Link>
            {' · '}
            <Link to="/today">Today</Link>
          </p>
        </>
      )}
    </div>
  );
}
